import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw, 
  Diamond, 
  Check, 
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Search
} from 'lucide-react';
import AdminApi from '../../apis/adminApis';
import PlanFormModal from './modals/PlanFormModal';
import PlanDeleteModal from './modals/PlanDeleteModal';

interface Feature {
  id: number;
  name: string;
}

interface Plan {
  id: number;
  plan_name: string;
  description: string;
  monthly_price: number;
  yearly_price: number;
  features: Feature[];
  is_active: boolean;
  is_featured: boolean;
  memory_limit: any;
  memory_unit:string;
  recipient_limit: number;
  created_at: string;
  updated_at: string;
}

interface PaginationInfo {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

const PlansManagement: React.FC = () => {
  // State
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0
  });
  
  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Fetch plans
  const fetchPlans = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await AdminApi.getPlans();
      
      if (response.data.status === 'success') {
        setPlans(response.data.data);
        
        // Handle pagination if it's provided in the response
        if (response.data.meta) {
          setPagination({
            currentPage: response.data.meta.current_page,
            lastPage: response.data.meta.last_page,
            perPage: response.data.meta.per_page,
            total: response.data.meta.total
          });
        }
      } else {
        setError('Failed to fetch plans. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching plans. Please try again.');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchPlans();
  }, []);
  
  // Handle search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPlans(1);
    }, 500); // Debounce for better performance
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Handle plan creation
  const handleCreatePlan = async (planData: Partial<Plan>) => {
    try {
      const response = await AdminApi.createPlan(planData);
      
      if (response.data.status === 'success') {
        setIsFormModalOpen(false);
        fetchPlans(pagination.currentPage);
      } else {
        setError('Failed to create plan. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating plan. Please try again.');
      console.error('Error creating plan:', err);
    }
  };
  
  // Handle plan update
  const handleUpdatePlan = async (planId: number, planData: Partial<Plan>) => {
    try {
      const response = await AdminApi.updatePlan(planId, planData);
      
      if (response.data.status === 'success') {
        setIsFormModalOpen(false);
        fetchPlans(pagination.currentPage);
      } else {
        setError('Failed to update plan. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating plan. Please try again.');
      console.error('Error updating plan:', err);
    }
  };
  
  // Handle plan deletion
  const handleDeletePlan = async (planId: number) => {
    try {
      const response = await AdminApi.deletePlan(planId);
      
      if (response.data.status === 'success') {
        setIsDeleteModalOpen(false);
        
        // If we delete the last item on the current page, go to previous page
        if (plans.length === 1 && pagination.currentPage > 1) {
          fetchPlans(pagination.currentPage - 1);
        } else {
          fetchPlans(pagination.currentPage);
        }
      } else {
        setError('Failed to delete plan. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting plan. Please try again.');
      console.error('Error deleting plan:', err);
    }
  };
  
  // Format price
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };
  
  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Handle page change
  const handlePageChange = (page: number) => {
    fetchPlans(page);
  };
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-xl font-semibold text-gray-800">Plans Management</h2>
          <button
            onClick={() => {
              setSelectedPlan(null);
              setIsEditMode(false);
              setIsFormModalOpen(true);
            }}
            className="mt-3 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Plan
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          
          <button
            onClick={() => fetchPlans(pagination.currentPage)}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-600">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        </div>
      )}
      
      {/* Plans table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pricing
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Storage
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
                  <p className="mt-2">Loading plans...</p>
                </td>
              </tr>
            ) : plans.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                  No plans found.
                </td>
              </tr>
            ) : (
              plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded bg-orange-100 flex items-center justify-center">
                        <Diamond className={`h-6 w-6 ${plan.is_featured ? 'text-orange-500' : 'text-gray-500'}`} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {plan.plan_name}
                          {plan.is_featured && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Featured
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {plan.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      Monthly: {formatPrice(plan.monthly_price)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Yearly: {formatPrice(plan.yearly_price)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.memory_limit} {plan.memory_unit}
                    </div>
                    <div className="text-xs text-gray-500">
                      {plan.recipient_limit} Recipient(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {plan.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <X className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(plan.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedPlan(plan);
                          setIsEditMode(true);
                          setIsFormModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPlan(plan);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {!loading && plans.length > 0 && pagination.lastPage > 1 && (
        <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === pagination.lastPage
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.perPage, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Page buttons */}
                {[...Array(pagination.lastPage)].map((_, i) => {
                  const page = i + 1;
                  // Show first page, last page, current page, and pages around current page
                  if (
                    page === 1 ||
                    page === pagination.lastPage ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === page
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                  
                  // Show ellipsis
                  if (
                    (page === 2 && pagination.currentPage > 3) ||
                    (page === pagination.lastPage - 1 && pagination.currentPage < pagination.lastPage - 2)
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.lastPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === pagination.lastPage
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {isFormModalOpen && (
        <PlanFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSubmit={isEditMode && selectedPlan ? 
            (data:any) => handleUpdatePlan(selectedPlan.id, data) : 
            handleCreatePlan
          }
          initialData={isEditMode && selectedPlan ? selectedPlan : undefined}
          isEditMode={isEditMode}
        />
      )}
      
      {isDeleteModalOpen && selectedPlan && (
        <PlanDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => handleDeletePlan(selectedPlan.id)}
          plan={selectedPlan}
        />
      )}
    </div>
  );
};

export default PlansManagement;